/**
 * 工具模式 / 侧栏展开钉住 / 调色板颜色线宽文字样式
 * 地图交互仍由 useMapDrawingEdit 持有；本 hook 提供面板与调色板状态（含 refs）
 */
import { useEffect, useRef, useState } from 'react';
import { applyMapToolCursor } from '../utils/mapDrawingBoard';
import { DRAFT_LINE_COLOR, DEFAULT_TEXT_STYLE } from '../utils/mapDrawingBoard/constants';

/**
 * @param {{ getMap?: Function, onActiveChange?: Function, tool: string|null }} opts
 */
export default function useMapDrawingTools({
  getMap,
  onActiveChange,
  tool
}) {
  const [expanded, setExpanded] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [drawColor, setDrawColor] = useState('#1976d2');
  const [drawOpacity, setDrawOpacity] = useState(0.85);
  const [sketchLineWidth, setSketchLineWidth] = useState(3);
  const [sketchPreviewColor, setSketchPreviewColor] = useState(DRAFT_LINE_COLOR);
  const [textStyle, setTextStyle] = useState({ ...DEFAULT_TEXT_STYLE });
  const [textContent, setTextContent] = useState('请输入说明文字');
  const [textEditOpen, setTextEditOpen] = useState(false);

  const leaveTimerRef = useRef(null);
  const colorRef = useRef(drawColor);
  const opacityRef = useRef(drawOpacity);
  const sketchWidthRef = useRef(sketchLineWidth);
  const sketchPreviewColorRef = useRef(sketchPreviewColor);
  const textStyleRef = useRef(textStyle);
  const textContentRef = useRef(textContent);

  useEffect(() => { colorRef.current = drawColor; }, [drawColor]);
  useEffect(() => { opacityRef.current = drawOpacity; }, [drawOpacity]);
  useEffect(() => { sketchWidthRef.current = sketchLineWidth; }, [sketchLineWidth]);
  useEffect(() => { sketchPreviewColorRef.current = sketchPreviewColor; }, [sketchPreviewColor]);
  useEffect(() => { textStyleRef.current = textStyle; }, [textStyle]);
  useEffect(() => { textContentRef.current = textContent; }, [textContent]);

  // 与原组件一致：有活动工具时通知父级（drawingToolsActiveRef）
  useEffect(() => {
    onActiveChange?.(!!tool);
    const map = getMap?.();
    if (!map || !map.doubleClickZoom) return undefined;
    if (tool && ['route', 'measure', 'polygon'].includes(tool)) {
      map.doubleClickZoom.disable();
    } else {
      map.doubleClickZoom.enable();
    }
    applyMapToolCursor(map, tool);
    return () => {
      try { map.doubleClickZoom.enable(); } catch (_) { /* ignore */ }
      applyMapToolCursor(map, null);
    };
  }, [tool, onActiveChange, getMap]);

  const handleMouseEnter = () => {
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
    setExpanded(true);
  };

  const handleMouseLeave = () => {
    if (pinned) return;
    leaveTimerRef.current = setTimeout(() => setExpanded(false), 480);
  };

  useEffect(() => () => {
    if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
  }, []);

  useEffect(() => {
    const collapse = () => {
      setPinned(false);
      setExpanded(false);
    };
    window.addEventListener('zhxt-collapse-map-side-tools', collapse);
    return () => window.removeEventListener('zhxt-collapse-map-side-tools', collapse);
  }, []);

  return {
    expanded, setExpanded,
    pinned, setPinned,
    leaveTimerRef,
    handleMouseEnter, handleMouseLeave,
    drawColor, setDrawColor,
    drawOpacity, setDrawOpacity,
    sketchLineWidth, setSketchLineWidth,
    sketchPreviewColor, setSketchPreviewColor,
    textStyle, setTextStyle,
    textContent, setTextContent,
    textEditOpen, setTextEditOpen,
    colorRef, opacityRef,
    sketchWidthRef, sketchPreviewColorRef,
    textStyleRef, textContentRef
  };
}
